import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { Functie } from '../../model';
import { ContentService } from '../../services/content/content.service';
import { Subscription } from 'rxjs';
import { UtilService } from '../../services/util/util.service';
import { FunctieCardComponent } from '../functie/card/functie-card.component';
import { Opleiding } from '../../model/opleiding/opleiding';
import { ProfileStorageService } from '../../services/profile/profile-storage.service';
import { FavorietenResource } from '../../services/resources/favorieten.resource';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { FavorietenChangeRequest } from '../../model/favoriet/favoriet';
import { StorageService } from '../../services/storage/storage.service';

const content = require('./lane.content.json');

@Component({
  selector: 'app-lane',
  templateUrl: './lane.component.html',
  styleUrls: ['./lane.component.scss']
})
export class LaneComponent implements OnInit, OnDestroy {
  @ViewChild('lane', { static: true, read: ElementRef })
  public lane: ElementRef;

  @ViewChild('laneContainer', { static: true, read: ElementRef })
  public laneContainer: ElementRef;

  @ViewChildren(FunctieCardComponent) fcc: QueryList<FunctieCardComponent>;

  @Input()
  public label: string = '';

  @Input()
  public mode: string = 'functie';

  @Input()
  public functieCardMode: string = 'DEFAULT';

  @Input()
  public opleidingCardMode: string = 'DEFAULT';

  @Input()
  set functies(functies: Functie[]) {
    this._functies = functies;

    if (this.thresholdMin) {
      this._functies = this._functies.filter(functie => {
        this.functieCount = this._functies.length;
        return functie.matchPercentage >= this.thresholdMin;
      });
    }

    if (this.thresholdMax) {
      this._functies = this._functies.filter(functie => {
        this.functieCount = this._functies.length;
        return functie.matchPercentage <= this.thresholdMax;
      });
    }

    if (this.thresholdMin && this.thresholdMax) {
      this._functies = this._functies.filter(functie => {
        this.functieCount = this._functies.length;
        return functie.matchPercentage >= this.thresholdMin && functie.matchPercentage <= this.thresholdMax;
      });
    }
  }

  public _functies: Functie[] = [];

  @Input()
  public opleidingen: Opleiding[] = [];

  @Input()
  public showMatchPercentage: boolean = false;

  @Input()
  public thresholdMin: number;

  @Input()
  public thresholdMax: number;

  @Output()
  public select: EventEmitter<Functie> = new EventEmitter();

  @Output()
  public favoriteChange: EventEmitter<void> = new EventEmitter();

  public functieCount: number;
  public moreLeftSide: boolean = true;
  public moreRightSide: boolean = true;

  public maxCardsShown: number = 4;
  // Card width + margins
  public functieCardWidth: number = 336;

  public lengthOfItems: number = 10;
  public pageSize: number = 4;
  public pageSizeOptionsOfPaginator = [4, 8, 10];

  public config: any;

  public isMobile: boolean = false;
  public isLoggedIn: boolean = false;
  public subscriptions: Subscription[] = [];

  public constructor(
    private contentService: ContentService,
    private utilService: UtilService,
    private profileStorageService: ProfileStorageService,
    private storageService: StorageService,
    private favorietenResource: FavorietenResource,
    private matDialog: MatDialog
  ) {
    contentService.loadContent(content);

    this.config = {
      currentPage: 1,
      itemsPerPage: 4
    };
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.profileStorageService.loggedInSubject.subscribe((isLoggedIn: boolean) => {
        if (this.isLoggedIn !== isLoggedIn) {
          this.isLoggedIn = isLoggedIn;
        }
      })
    );

    this.subscriptions.push(
      this.utilService.getScreenMode$().subscribe(screenMode => {
        this.isMobile = screenMode.includes('MOBILE');
      })
    );
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }


  public scrollToTheLeft() {
    this.laneContainer.nativeElement.scrollLeft -= this.functieCardWidth * this.maxCardsShown;
  }

  public scrollToTheRight() {
    this.laneContainer.nativeElement.scrollLeft += this.functieCardWidth * this.maxCardsShown;
  }

  public handleSelect($event: any): void {
    this.select.emit($event);
  }

  public handleFavoriteClick(object: Functie | Opleiding, isFunctie: boolean = true): void {
    if (this.isLoggedIn) {
      this.processFavorite(object, isFunctie);
    } else {
      this.storageService.set(
        'lbp-pending-favorite',
        {
          isFunctie: isFunctie,
          id: object.id
        },
        true
      );
      this.openLoginModal();
    }
  }

  private processFavorite(object: Functie | Opleiding, isFunctie: boolean): void {
    if (object.favoriet) {
      this.favorietenResource
        .deleteFavorietenChange({
          functies: isFunctie ? [object.id + ''] : [],
          opleidingen: !isFunctie ? [object.id + ''] : []
        } as FavorietenChangeRequest)
        .subscribe(() => {
          object.favoriet = false;

          this.favoriteChange.emit();
        });
    } else {
      this.favorietenResource
        .postFavorietenChange({
          functies: isFunctie ? [object.id + ''] : [],
          opleidingen: !isFunctie ? [object.id + ''] : []
        } as FavorietenChangeRequest)
        .subscribe(() => {
          object.favoriet = true;

          this.favoriteChange.emit();
        });
    }
  }

  private async openLoginModal(): Promise<void> {
    const { LoginModalComponent } = await import('../account/login-modal/login-modal.component');

    const config: MatDialogConfig = this.isMobile
      ? {
        height: '100%',
        width: '100%',
        maxHeight: '100%',
        maxWidth: '100%'
      }
      : {
        width: '380px',
        maxWidth: '100%'
      };

    const ref: MatDialogRef<any> = this.matDialog.open(LoginModalComponent, config);
    ref.componentInstance.rerouteEnabled = true;
    ref.componentInstance.introMessage = this.contentService.getContent('lane.loginIntroMessage');
  }
}
